/**
 * Case Study (vaka çalışması) kategorisinde adayın sınav boyunca sol panelde gördüğü vaka materyali.
 * QuestionCollection.CaseBrief kolonunda JSON string olarak saklanır.
 * Aynı yapı Vinter-Applicant tarafında da (models/case-brief.ts) okunur; iki taraf birbiriyle uyumlu kalmalı.
 */

export const CASE_BRIEF_VERSION = 1;

export type CaseBriefBlockType = 'text' | 'table';

export interface CaseBriefTextBlock {
    type: 'text';
    title: string;
    /** Zengin metin (p-editor çıktısı, sınırlı HTML). */
    html: string;
}

export interface CaseBriefTableBlock {
    type: 'table';
    title: string;
    /** Tablonun hemen üstünde gösterilen açıklama metni (opsiyonel, düz metin). */
    description?: string;
    columns: string[];
    rows: string[][];
}

export type CaseBriefBlock = CaseBriefTextBlock | CaseBriefTableBlock;

export interface CaseBrief {
    version: number;
    blocks: CaseBriefBlock[];
}

export function createEmptyCaseBrief(): CaseBrief {
    return { version: CASE_BRIEF_VERSION, blocks: [] };
}

export function createTextBlock(title: string = '', html: string = ''): CaseBriefTextBlock {
    return { type: 'text', title, html };
}

export function createTableBlock(title: string = '', columnCount: number = 2, rowCount: number = 1): CaseBriefTableBlock {
    const columns = Array.from({ length: columnCount }, () => '');
    const rows = Array.from({ length: rowCount }, () => Array.from({ length: columnCount }, () => ''));
    return { type: 'table', title, description: '', columns, rows };
}

/** Bozuk/eski veri gelse bile güvenli bir CaseBrief döndürür. */
export function parseCaseBrief(raw: string | null | undefined): CaseBrief {
    if (!raw || typeof raw !== 'string') {
        return createEmptyCaseBrief();
    }
    try {
        const parsed = JSON.parse(raw);
        const blocks: CaseBriefBlock[] = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
        return {
            version: Number(parsed?.version) || CASE_BRIEF_VERSION,
            blocks: blocks.map(normalizeBlock).filter((b): b is CaseBriefBlock => b !== null)
        };
    } catch {
        return createEmptyCaseBrief();
    }
}

export function serializeCaseBrief(brief: CaseBrief | null | undefined): string {
    if (!brief) {
        return '';
    }
    const cleaned: CaseBrief = {
        version: CASE_BRIEF_VERSION,
        blocks: (brief.blocks ?? []).map(normalizeBlock).filter((b): b is CaseBriefBlock => b !== null)
    };
    return JSON.stringify(cleaned);
}

/** En az bir dolu blok var mı? Boş metin/tablo blokları "içerik" sayılmaz. */
export function hasCaseBriefContent(brief: CaseBrief | null | undefined): boolean {
    if (!brief?.blocks?.length) {
        return false;
    }
    return brief.blocks.some(block => {
        if (block.type === 'text') {
            return stripHtml(block.html).length > 0 || (block.title ?? '').trim().length > 0;
        }
        return (block.description ?? '').trim().length > 0
            || block.columns.some(c => (c ?? '').trim().length > 0)
            || block.rows.some(r => r.some(c => (c ?? '').trim().length > 0));
    });
}

function normalizeBlock(block: any): CaseBriefBlock | null {
    if (!block || typeof block !== 'object') {
        return null;
    }
    if (block.type === 'table') {
        const columns: string[] = Array.isArray(block.columns) ? block.columns.map(toText) : [];
        const rows: string[][] = Array.isArray(block.rows)
            ? block.rows.map((r: any) => Array.isArray(r) ? r.map(toText) : [])
            : [];
        // Satır uzunluklarını sütun sayısına eşitle.
        const width = columns.length;
        const fixedRows = rows.map(r => {
            const copy = r.slice(0, width);
            while (copy.length < width) { copy.push(''); }
            return copy;
        });
        return { type: 'table', title: toText(block.title), description: toText(block.description), columns, rows: fixedRows };
    }
    if (block.type === 'text') {
        return { type: 'text', title: toText(block.title), html: toText(block.html) };
    }
    return null;
}

function toText(value: any): string {
    return value === null || value === undefined ? '' : String(value);
}

function stripHtml(html: string): string {
    return (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}
